import { useEffect, useState } from 'react';
import { AllUser } from '@/interfaces/user';
import { editUserDetails, getUserByToken } from '@/services/userService';
import { getDynamicEnvVarsInClient } from '@/utils/envHelper';
import { getCookie, setCookie } from 'cookies-next';
import { toast } from 'sonner';

const useLanguageConflictChecker = ({
  accessToken,
  tenantId,
  tenantName,
}: {
  accessToken: string;
  tenantId: string;
  tenantName: string;
}) => {
  const [user, setUser] = useState<AllUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const NEXT_LOCALE = getCookie('NEXT_LOCALE');
  const [savedLanguage, setSavedLanguage] = useState<string | null>(null);
  const [isLanguageConflict, setIsLanguageConflict] = useState<null | boolean>(
    null
  );

  const languageUpdate = async ({ userId }: { userId: string }) => {
    try {
      const updateValues = {
        language_preference: NEXT_LOCALE,
      };

      const response = await editUserDetails(
        userId,
        updateValues,
        tenantId,
        tenantName,
        accessToken
      );

      if (response.status === 201) {
        setCookie('NEXT_LOCALE', NEXT_LOCALE, {
          path: '/auth/ui',
        });
        setCookie('NEXT_LOCALE', NEXT_LOCALE, {
          path: '/',
        });
        setSavedLanguage(NEXT_LOCALE as string);
        setIsLanguageConflict(false);
      } else {
        toast.error('Failed to update language preference');
      }
    } catch (error) {
      console.error('Error updating language preference:', error);
      toast.error('Failed to update language preference');
    } finally {
      setIsLanguageConflict(false);
    }
  };

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const { NEXT_PUBLIC_CW_AUTH_URL } = getDynamicEnvVarsInClient();

        const userData = await getUserByToken(
          tenantId,
          tenantName,
          accessToken,
          NEXT_PUBLIC_CW_AUTH_URL
        );

        if (userData) {
          setUser(userData);
          if (userData.user_preference?.language_preference) {
            setSavedLanguage(userData.user_preference?.language_preference);
            if (userData.user_preference?.language_preference !== NEXT_LOCALE) {
              setIsLanguageConflict(true);
            } else {
              setIsLanguageConflict(false);
            }
          } else {
            languageUpdate({ userId: userData.id });
          }
        } else {
          setIsLanguageConflict(false);
          setIsLoading(false);
        }
      } catch (error) {
        console.error('Failed to fetch user data:', error);
      } finally {
        setIsLoading(false);
      }
    };

    if (accessToken && tenantId && tenantName) {
      fetchUserData();
    }
  }, [accessToken, tenantId, tenantName, NEXT_LOCALE]);

  return {
    isLanguageConflict,
    isLoading,
    savedLanguage,
    currentLanguage: NEXT_LOCALE,
    user,
  };
};
export default useLanguageConflictChecker;
